import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api, useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Save, Send, Loader2, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MachineToolCard } from '@/components/MachineToolCard';

const statusColors = {
  Draft: 'bg-slate-100 text-slate-700 border-slate-300',
  Submitted: 'bg-blue-100 text-blue-700 border-blue-300',
  Approved: 'bg-green-100 text-green-700 border-green-300',
  Rejected: 'bg-red-100 text-red-700 border-red-300',
};

import { computePhaseLife, computeToolStats } from '@/utils/toolLifeCalculations';
import { getPreviousConsecutiveShift as getPrevShift, computeLeftOverFromInsertCounts } from '@/utils/toolInspectionSummary';

const SETUP_TO_OPERATION = { 'I': 'OBR', 'II': 'OTR' };

export default function ToolInspectionPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [date, setDate] = useState(searchParams.get('date') || new Date().toISOString().split('T')[0]);
  const [shift, setShift] = useState(searchParams.get('shift') || 'Day');

  const [machines, setMachines] = useState([]);
  const [parts, setParts] = useState([]);
  const [toolMasters, setToolMasters] = useState([]);
  const [insertTypes, setInsertTypes] = useState([]);
  const [setupStages, setSetupStages] = useState({});
  const [toolLifeStandards, setToolLifeStandards] = useState([]);
  const [existingLogs, setExistingLogs] = useState({});
  const [machineData, setMachineData] = useState({});
  // Only one machine expanded at a time
  const [expandedMachineId, setExpandedMachineId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [logsLoadError, setLogsLoadError] = useState(null);
  /** Issued from store per insert type (for summary card; loaded from first log's summary, edited in UI). */
  const [issuedByInsertType, setIssuedByInsertType] = useState({});
  /** Left over from previous consecutive shift (Submitted/Approved); read-only, one value per insert type. */
  const [leftOverByInsertType, setLeftOverByInsertType] = useState({});
  /** Manual overrides for "used" per insert type; falls back to auto-computed when empty. */
  const [usedOverrideByInsertType, setUsedOverrideByInsertType] = useState({});

  /** Auto-computed inserts used per insert type from checkpoint data. */
  const usedByInsertType = useMemo(() => {
    const map = {};
    for (const data of Object.values(machineData)) {
      for (const tool of data.tools || []) {
        const code = tool.insert_type?.trim?.() || tool.insert_type;
        if (!code || !(tool.tool_code?.trim?.() || tool.tool_code)) continue;
        const total = tool.total_inserts_used ?? 0;
        map[code] = (map[code] || 0) + total;
      }
    }
    return map;
  }, [machineData]);

  /** Effective "used" values: manual override takes precedence over auto-computed. */
  const effectiveUsedByInsertType = useMemo(() => {
    const merged = { ...usedByInsertType };
    for (const [code, val] of Object.entries(usedOverrideByInsertType)) {
      if (val !== '' && val !== undefined) merged[code] = Number(val) || 0;
    }
    return merged;
  }, [usedByInsertType, usedOverrideByInsertType]);

  useEffect(() => {
    const prev = getPrevShift(date, shift);
    if (!prev) {
      setLeftOverByInsertType({});
      return;
    }
    api.get('/shift-logs', { params: { date: prev.date, shift: prev.shift } })
      .then(res => {
        const eligible = (res.data || []).filter(l => l.status === 'Submitted' || l.status === 'Approved');
        const firstWithSummary = eligible.find(l => l.summary?.insert_counts?.length > 0);
        if (!firstWithSummary) {
          setLeftOverByInsertType({});
          return;
        }
        setLeftOverByInsertType(computeLeftOverFromInsertCounts(firstWithSummary.summary.insert_counts));
      })
      .catch(() => setLeftOverByInsertType({}));
  }, [date, shift]);

  useEffect(() => {
    Promise.all([
      api.get('/machines'),
      api.get('/parts'),
      api.get('/tool-master'),
      api.get('/insert-type-master'),
      api.get('/machines/setup-stages'),
      api.get('/tool-life-standards'),
    ]).then(([machinesRes, partsRes, toolsRes, insertsRes, setupRes, standardsRes]) => {
      // Show all machines (active + inactive); validation only requires data for active ones
      setMachines(machinesRes.data || []);
      setParts(partsRes.data);
      setToolMasters(toolsRes.data);
      setInsertTypes(insertsRes.data);
      setSetupStages(setupRes.data);
      setToolLifeStandards(standardsRes.data || []);
      setLoading(false);
    }).catch(() => {
      toast.error('Failed to load master data');
      setLoading(false);
    });
  }, []);

  const loadExistingLogs = useCallback(async () => {
    if (!date || !shift || machines.length === 0) return;
    setIssuedByInsertType({});
    setUsedOverrideByInsertType({});
    try {
      const res = await api.get('/shift-logs', { params: { date, shift } });
      const logsMap = {};
      const dataMap = {};

      res.data.forEach(log => {
        logsMap[log.machine_id] = log;
        const op = log.operation || 'OBR';
        dataMap[log.machine_id] = {
          partNoId: log.part_no_id || '',
          tools: log.tools || [],
          setupStage: op === 'OTR' ? 'II' : 'I',
          operation: op,
        };
      });

      const firstWithSummary = res.data.find(l => l.summary?.insert_counts?.length > 0);
      if (firstWithSummary) {
        const issued = {};
        const usedOverrides = {};
        firstWithSummary.summary.insert_counts.forEach(ic => {
          if (ic.insert_type_code != null && ic.insert_type_code !== '') {
            issued[ic.insert_type_code] = Math.max(0, Number(ic.quantity_issued) || 0);
            usedOverrides[ic.insert_type_code] = Math.max(0, Number(ic.quantity_used) || 0);
          }
        });
        setIssuedByInsertType(issued);
        setUsedOverrideByInsertType(usedOverrides);
      }

      setExistingLogs(logsMap);
      // Reset machine data to only this date/shift: each machine gets API data or empty (no stale data from other dates)
      setMachineData(() => {
        const next = {};
        machines.forEach(m => {
          next[m.id] = dataMap[m.id] ?? { partNoId: '', tools: [] };
        });
        return next;
      });
      setExpandedMachineId(null);
      setLogsLoadError(null);
    } catch (error) {
      const status = error?.response?.status;
      const isNoLogs = status === 404 || (error?.response?.data?.detail && typeof error.response.data.detail === 'string' && error.response.data.detail.toLowerCase().includes('no log'));
      if (isNoLogs) {
        setExistingLogs({});
        setIssuedByInsertType({});
        setUsedOverrideByInsertType({});
        setMachineData(() => {
          const next = {};
          machines.forEach(m => {
            next[m.id] = { partNoId: '', tools: [] };
          });
          return next;
        });
        setLogsLoadError(null);
        return;
      }
      console.error('Failed to load shift logs', error);
      setLogsLoadError(error?.response?.data?.detail || error?.message || 'Failed to load shift logs');
      toast.error('Failed to load shift logs');
    }
  }, [date, shift, machines]);

  useEffect(() => {
    if (machines.length > 0) loadExistingLogs();
  }, [loadExistingLogs, machines.length]);

  const getDefaultSetupByPosition = (machineId) => {
    const idx = machines.findIndex(m => m.id === machineId);
    return idx >= 0 && idx % 2 === 1 ? 'II' : 'I';
  };

  const getSetupStage = (machineId) => {
    const d = machineData[machineId];
    if (d?.setupStage) return d.setupStage;
    return setupStages[machineId] ?? getDefaultSetupByPosition(machineId);
  };

  const getOperation = (machineId) => {
    const d = machineData[machineId];
    if (d?.operation) return d.operation;
    return SETUP_TO_OPERATION[getSetupStage(machineId)] || 'OBR';
  };
  const getMachineStatus = (machineId) => existingLogs[machineId]?.status || null;
  const isFormLocked = (machineId) => {
    const s = getMachineStatus(machineId);
    return s === 'Approved' || s === 'Submitted';
  };

  const toggleMachine = (machineId) => {
    setExpandedMachineId(prev => (prev === machineId ? null : machineId));
  };

  const updateMachineField = (machineId, field, value) => {
    setMachineData(prev => {
      const base = prev[machineId] || { partNoId: '', tools: [] };
      const next = { ...base, [field]: value };
      if (field === 'setupStage') {
        next.operation = SETUP_TO_OPERATION[value] || 'OBR';
      }
      return { ...prev, [machineId]: next };
    });
  };

  const addTool = (machineId) => {
    setMachineData(prev => {
      const cur = prev[machineId] || { partNoId: '', tools: [] };
      return { ...prev, [machineId]: { ...cur, tools: [...cur.tools, { tool_code: '', insert_type: '', checkpoints: [], total_inserts_used: 1, average_phase_life: null }] } };
    });
    setExpandedMachineId(machineId);
  };

  const removeTool = (machineId, ti) => {
    setMachineData(prev => {
      const cur = prev[machineId];
      if (!cur) return prev;
      const tools = [...cur.tools];
      tools.splice(ti, 1);
      return { ...prev, [machineId]: { ...cur, tools } };
    });
  };

  const updateTool = (machineId, ti, field, value) => {
    setMachineData(prev => {
      const cur = prev[machineId];
      if (!cur) return prev;
      const tools = [...cur.tools];
      tools[ti] = { ...tools[ti], [field]: value };
      return { ...prev, [machineId]: { ...cur, tools } };
    });
  };

  const addCheckpoint = (machineId, ti) => {
    setMachineData(prev => {
      const cur = prev[machineId];
      if (!cur) return prev;
      const tools = [...cur.tools];
      const tool = { ...tools[ti] };
      const totalPhases = insertTypes.find(it => it.insert_type_code === tool.insert_type)?.total_phases ?? 6;
      tool.checkpoints = [...tool.checkpoints, { qty: undefined, insert_phase: undefined, total_phases: totalPhases, new_insert_added: false, phase_life_computed: null }];
      tools[ti] = tool;
      return { ...prev, [machineId]: { ...cur, tools } };
    });
  };

  const removeCheckpoint = (machineId, ti, ci) => {
    setMachineData(prev => {
      const cur = prev[machineId];
      if (!cur) return prev;
      const tools = [...cur.tools];
      const tool = { ...tools[ti] };
      tool.checkpoints = [...tool.checkpoints];
      tool.checkpoints.splice(ci, 1);
      // Recompute stats
      const stats = computeToolStats(tool.checkpoints);
      tool.total_inserts_used = stats.totalInserts;
      tool.average_phase_life = stats.avgLife;
      tools[ti] = tool;
      return { ...prev, [machineId]: { ...cur, tools } };
    });
  };

  const updateCheckpoint = (machineId, ti, ci, field, value) => {
    setMachineData(prev => {
      const cur = prev[machineId];
      if (!cur) return prev;
      const tools = [...cur.tools];
      const tool = { ...tools[ti] };
      tool.checkpoints = [...tool.checkpoints];
      tool.checkpoints[ci] = { ...tool.checkpoints[ci], [field]: value };
      for (let i = Math.max(1, ci); i < tool.checkpoints.length; i++) {
        tool.checkpoints[i] = { ...tool.checkpoints[i], phase_life_computed: computePhaseLife(tool.checkpoints, i) };
      }
      const stats = computeToolStats(tool.checkpoints);
      tool.total_inserts_used = stats.totalInserts;
      tool.average_phase_life = stats.avgLife;
      tools[ti] = tool;
      return { ...prev, [machineId]: { ...cur, tools } };
    });
  };

  const saveDrafts = async () => {
    setSaving(true);
    let savedCount = 0;
    try {
      const hasAnyIssuedData = (insertTypes || []).some(
        it => (Number(issuedByInsertType[it.insert_type_code]) || 0) > 0
      );

      for (const machine of machines) {
        const data = machineData[machine.id];
        const status = getMachineStatus(machine.id);
        if (status === 'Submitted' || status === 'Approved') continue;

        const hasToolsWithCheckpoints = data?.tools?.some(t => t.tool_code && t.checkpoints.length > 0);
        if (!hasToolsWithCheckpoints && !hasAnyIssuedData) continue;

        const existingLog = existingLogs[machine.id];
        const toolsPayload = hasToolsWithCheckpoints
          ? (data.tools || []).map(t => ({
              ...t,
              tool_code: t.tool_code || '',
              insert_type: t.insert_type || '',
              total_inserts_used: typeof t.total_inserts_used === 'number' ? t.total_inserts_used : 1,
              checkpoints: (t.checkpoints || []).map(cp => {
                const phase = cp.insert_phase ?? 1;
                const qty = typeof cp.qty === 'number' ? cp.qty : 0;
                const totalPhases = insertTypes.find(it => it.insert_type_code === t.insert_type)?.total_phases ?? cp.total_phases ?? 6;
                return {
                  ...cp,
                  qty,
                  insert_phase: phase,
                  total_phases: totalPhases,
                  new_insert_added: phase === 1,
                };
              }),
            }))
          : [];

        const summaryPayload = {
          insert_counts: (insertTypes || []).map(it => ({
            insert_type_code: it.insert_type_code || '',
            quantity_issued: Math.max(0, Number(issuedByInsertType[it.insert_type_code]) || 0),
            quantity_used: Math.max(0, Number(effectiveUsedByInsertType[it.insert_type_code]) || 0),
          })),
        };

        try {
          if (existingLog) {
            await api.put('/shift-logs/' + existingLog.id, {
              shift_incharge: user?.name ?? '',
              part_no_id: data?.partNoId || '',
              tools: toolsPayload,
              summary: summaryPayload,
            });
          } else {
            const res = await api.post('/shift-logs', {
              date, shift, shift_incharge: user?.name ?? '',
              machine_id: machine.id, part_no_id: data?.partNoId || '',
              operation: getOperation(machine.id),
              tools: toolsPayload,
              summary: summaryPayload,
            });
            if (res.data?.id) {
              setExistingLogs(prev => ({ ...prev, [machine.id]: res.data }));
            }
          }
          savedCount++;
        } catch (err) {
          if (err.response?.status === 409) {
            const existingId = err.response.data?.existing_id;
            if (existingId) {
              await api.put('/shift-logs/' + existingId, {
                shift_incharge: user?.name ?? '',
                part_no_id: data?.partNoId || '',
                tools: toolsPayload,
                summary: summaryPayload,
              });
              savedCount++;
            }
          }
        }
      }
      if (savedCount > 0) {
        toast.success('Saved ' + savedCount + ' machine log(s) as draft');
        await loadExistingLogs();
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Save failed';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  const validateForSubmit = () => {
    const errors = [];
    const machinesWithoutCheckpoint = [];
    const activeMachines = machines.filter(m => m.active === true);

    for (const machine of activeMachines) {
      const data = machineData[machine.id];
      const code = machine.code || machine.id;
      const totalCheckpoints = (data?.tools ?? []).reduce((sum, t) => sum + (t.checkpoints?.length ?? 0), 0);
      if (totalCheckpoints === 0) {
        machinesWithoutCheckpoint.push(code);
        continue;
      }

      const hasToolData = data?.tools?.some(t => t.tool_code && (t.checkpoints?.length ?? 0) > 0);
      if (!hasToolData) continue;

      if (!data.partNoId?.trim()) errors.push(`Machine ${code}: Part No is required`);
      if (!data.tools?.length) errors.push(`Machine ${code}: At least one tool is required`);

      for (let ti = 0; ti < (data.tools?.length ?? 0); ti++) {
        const tool = data.tools[ti];
        if (!tool.tool_code && (tool.checkpoints?.length ?? 0) > 0) {
          errors.push(`Machine ${code}: Tool is required for tool row ${ti + 1}`);
        }
        if (tool.tool_code && !tool.insert_type?.trim()) {
          errors.push(`Machine ${code}: Insert type is required for tool ${tool.tool_code}`);
        }
        const checkpoints = tool.checkpoints ?? [];
        if (checkpoints.length === 0 && tool.tool_code) {
          errors.push(`Machine ${code}, tool ${tool.tool_code}: At least one checkpoint is required`);
        }
        const totalPhases = insertTypes.find(it => it.insert_type_code === tool.insert_type)?.total_phases ?? 6;
        let prevQty = 0;
        for (let ci = 0; ci < checkpoints.length; ci++) {
          const cp = checkpoints[ci];
          const qty = cp.qty ?? 0;
          if (qty < 0) errors.push(`Machine ${code}: Checkpoint ${ci + 1} QTY cannot be negative`);
          if (ci > 0 && qty <= prevQty) {
            errors.push(`Machine ${code}: Checkpoint ${ci + 1} QTY must be greater than previous (${prevQty})`);
          }
          prevQty = qty;
          const phase = cp.insert_phase;
          if (phase == null || phase === '' || phase < 1 || phase > totalPhases) {
            errors.push(`Machine ${code}: Checkpoint ${ci + 1} Phase is required (1–${totalPhases})`);
          }
        }
      }
    }

    if (machinesWithoutCheckpoint.length > 0) {
      errors.push(machinesWithoutCheckpoint.join(', ') + '. Fill at least one checkpoint.');
    }

    return errors;
  };

  const submitAll = async () => {
    const errors = validateForSubmit();
    if (errors.length > 0) {
      toast.error(errors[0]);
      if (errors.length > 1) toast.error(errors.slice(1).join(' • '), { duration: 5000 });
      return;
    }
    setSubmitting(true);
    await saveDrafts();
    let submitted = 0;
    try {
      const res = await api.get('/shift-logs', { params: { date, shift } });
      for (const log of res.data) {
        if (log.status === 'Draft') {
          await api.patch('/shift-logs/' + log.id + '/submit');
          submitted++;
        }
      }
      if (submitted > 0) {
        toast.success('Submitted ' + submitted + ' log(s) for approval');
        window.dispatchEvent(new CustomEvent('approvals-changed'));
      } else toast.info('No draft logs to submit');
      await loadExistingLogs();
    } catch {
      toast.error('Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  const hasAnyDraft = Object.values(existingLogs).some(l => l.status === 'Draft');
  const hasAnyNewData = Object.entries(machineData).some(function(entry) {
    var d = entry[1];
    return d?.tools?.some(t => t.tool_code && t.checkpoints.length > 0);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="tool-inspection-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const statusSummary = Object.values(existingLogs);
  const uniqueStatuses = [...new Set(statusSummary.map(l => l.status))];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tool-inspection-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Tool Life Monitoring</h1>
          <p className="text-slate-500">Inserts Consumption Sheet</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/tool-inspection/history', { state: { status: 'Draft' } })}
        >
          View all drafts
        </Button>
      </div>

      <Card className="border-slate-200">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label>Date</Label>
              <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="h-10 w-40" data-testid="tool-inspection-date" />
            </div>
            <div className="space-y-1.5">
              <Label>Shift</Label>
              <Select value={shift} onValueChange={setShift}>
                <SelectTrigger className="h-10" data-testid="tool-inspection-shift"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Day">Day</SelectItem>
                  <SelectItem value="Night">Night</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Shift Incharge</Label>
              <Input value={user?.name ?? ''} readOnly className="h-10 bg-slate-50" data-testid="tool-inspection-incharge" />
            </div>
            <div className="space-y-1.5 sm:col-span-2 lg:col-span-3">
              <Label>Status Summary</Label>
              <div className="flex flex-wrap gap-1.5 pt-1">
                {statusSummary.length === 0 ? (
                  <Badge variant="outline" className="bg-slate-50">No logs yet</Badge>
                ) : (
                  uniqueStatuses.map(s => (
                    <Badge key={s} variant="outline" className={statusColors[s] || ''}>
                      {s} ({statusSummary.filter(l => l.status === s).length})
                    </Badge>
                  ))
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {logsLoadError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 flex items-center justify-between gap-3" data-testid="logs-load-error">
          <span>{logsLoadError}</span>
          <Button variant="outline" size="sm" onClick={() => { setLogsLoadError(null); loadExistingLogs(); }} data-testid="logs-load-retry">
            Retry
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start" data-testid="machine-cards-grid">
        {machines.map(machine => (
          <MachineToolCard
            key={machine.id}
            machine={machine}
            operation={getOperation(machine.id)}
            status={getMachineStatus(machine.id)}
            locked={isFormLocked(machine.id)}
            expanded={expandedMachineId === machine.id}
            data={{
              ...(machineData[machine.id] || { partNoId: '', tools: [] }),
              setupStage: getSetupStage(machine.id),
              operation: getOperation(machine.id),
            }}
            parts={parts}
            toolMasters={toolMasters}
            insertTypes={insertTypes}
            toolLifeStandards={toolLifeStandards}
            onToggle={() => toggleMachine(machine.id)}
            onUpdateField={(field, value) => updateMachineField(machine.id, field, value)}
            onAddTool={() => addTool(machine.id)}
            onRemoveTool={(ti) => removeTool(machine.id, ti)}
            onUpdateTool={(ti, field, value) => updateTool(machine.id, ti, field, value)}
            onAddCheckpoint={(ti) => addCheckpoint(machine.id, ti)}
            onUpdateCheckpoint={(ti, ci, field, value) => updateCheckpoint(machine.id, ti, ci, field, value)}
            onRemoveCheckpoint={(ti, ci) => removeCheckpoint(machine.id, ti, ci)}
          />
        ))}
      </div>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg">Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-sm font-medium mb-2 block">Left over from previous shift</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {insertTypes.map(it => (
                <div key={it.insert_type_code} className="flex items-center gap-2">
                  <Label className="text-xs w-14 font-mono">{it.insert_type_code}</Label>
                  <span className="h-8 min-w-[4rem] flex items-center text-sm font-mono font-medium text-slate-600" data-testid={'insert-leftover-' + it.insert_type_code}>
                    {leftOverByInsertType[it.insert_type_code] ?? 0}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-sm font-medium mb-2 block">Total inserts issued</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {insertTypes.map(it => (
                <div key={it.insert_type_code} className="flex items-center gap-2">
                  <Label className="text-xs w-14 font-mono">{it.insert_type_code}</Label>
                  <Input
                    type="number"
                    min={0}
                    className="h-8 w-20 text-sm font-mono"
                    value={issuedByInsertType[it.insert_type_code] ?? ''}
                    onChange={e => {
                      const raw = e.target.value;
                      const v = raw === '' ? '' : Math.max(0, parseInt(raw, 10) || 0);
                      setIssuedByInsertType(prev => ({ ...prev, [it.insert_type_code]: v }));
                    }}
                    placeholder="0"
                    data-testid={'insert-issued-' + it.insert_type_code}
                  />
                </div>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-sm font-medium mb-2 block">Total counts used</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {insertTypes.map(it => (
                <div key={it.insert_type_code} className="flex items-center gap-2">
                  <Label className="text-xs w-14 font-mono">{it.insert_type_code}</Label>
                  <Input
                    type="number"
                    min={0}
                    className="h-8 w-20 text-sm font-mono"
                    value={usedOverrideByInsertType[it.insert_type_code] ?? usedByInsertType[it.insert_type_code] ?? ''}
                    onChange={e => {
                      const raw = e.target.value;
                      const v = raw === '' ? '' : Math.max(0, parseInt(raw, 10) || 0);
                      setUsedOverrideByInsertType(prev => ({ ...prev, [it.insert_type_code]: v }));
                    }}
                    placeholder="0"
                    data-testid={'insert-used-' + it.insert_type_code}
                  />
                </div>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-sm font-medium mb-2 block">Final count (left over + issued − used)</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {insertTypes.map(it => {
                const leftOver = leftOverByInsertType[it.insert_type_code] ?? 0;
                const issued = Number(issuedByInsertType[it.insert_type_code]) || 0;
                const used = effectiveUsedByInsertType[it.insert_type_code] ?? 0;
                const finalCount = Math.max(0, leftOver + issued - used);
                return (
                  <div key={it.insert_type_code} className="flex items-center gap-2">
                    <Label className="text-xs w-14 font-mono">{it.insert_type_code}</Label>
                    <span className="h-8 min-w-[4rem] flex items-center text-sm font-mono font-medium text-slate-700" data-testid={'insert-final-' + it.insert_type_code}>
                      {finalCount}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-3 justify-end pb-8" data-testid="tool-inspection-actions">
        <Button variant="outline" onClick={saveDrafts} disabled={saving || submitting} data-testid="save-draft-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save as Draft
        </Button>
        <Button onClick={submitAll} disabled={submitting || saving || !(hasAnyDraft || hasAnyNewData)} data-testid="submit-btn">
          {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
          Submit for Approval
        </Button>
      </div>
    </div>
  );
}
